import React, {useCallback, useEffect} from 'react'
import styled from '@emotion/styled'
import {useIsStreetViewFeatureEnabled} from 'hooks/useIsStreetViewFeatureEnabled'
import {OverlayType} from 'modules/overlay/types'

import {Button} from '@vio/atlas/Button'
import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import useShowOverlay from '../../../hooks/useShowOverlay/useShowOverlay'
import {HotelLocationContent} from '../HotelLocationContent'
import {StaticMapImage} from '../HotelLocationMap/StaticMap'
import {
  SelectedElement,
  TrackLocationClick
} from '../HotelLocationMap/TrackLocationClick'
import {LocationAddress} from './LocationAddress'

import type {ContentHotel} from '@findhotel/sapi'

const MAP_DESKTOP_HEIGHT = 320
const MAP_DESKTOP_WIDTH = 560

const Wrapper = styled.div<{isMobile?: boolean}>`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${({isMobile}) =>
    isMobile ? cssTheme.layout.spacing.s600 : cssTheme.layout.spacing.s900};

  ${linariaMq.desktopXs} {
    flex-direction: column-reverse;
    align-items: flex-start;
  }

  ${linariaMq.desktopSm} {
    align-items: flex-start;
    flex-direction: row-reverse;
  }
`

const LocationAddressWrapper = styled.div`
  width: 100%;
  &.--under-map {
    margin-top: ${cssTheme.layout.spacing.s400};
  }
`

const MapBoxWrapper = styled.div`
  height: 50vw;
  position: relative;
  width: 100%;
  border-radius: ${cssTheme.layout.radius.lg};
  cursor: pointer;
  overflow: hidden;
  ${linariaMq.desktopXs} {
    height: ${MAP_DESKTOP_HEIGHT}px;
  }
  ${linariaMq.desktopSm} {
    width: calc(50vw - ${cssTheme.layout.spacing.s600});
  }
  ${linariaMq.desktopMd} {
    width: ${MAP_DESKTOP_WIDTH}px;
  }
`

const ButtonPositioner = styled.div`
  display: flex;
  position: absolute;
  right: ${cssTheme.layout.spacing.s300};
  bottom: ${cssTheme.layout.spacing.s300};
  gap: ${cssTheme.layout.spacing.s300};
`

const LocationContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const LocationTitle = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s600};
  &.--only-address {
    margin-bottom: ${cssTheme.layout.spacing.s500};
  }
`

const StyledButtonLink = styled(ButtonLink)`
  width: fit-content;
  margin-top: ${cssTheme.layout.spacing.s500};
`

const StyledTrackLocationClick = styled(TrackLocationClick)`
  width: 100%;
  ${linariaMq.desktopSm} {
    width: auto;
  }
`

interface Props {
  hotel?: ContentHotel
}

export const HotelLocationDetails: React.FC<Props> = ({hotel}) => {
  const {isMobile, isDesktopXs, isDesktopSm} = useDeviceLayout()

  const {lon, lat} = hotel?._geoloc || {}
  const {location} = hotel?.descriptions || {}
  const {poi: attractions} = hotel?.location || {}
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const dispatchTeamEvent = useDispatchTeamEvent()
  const showOverlay = useShowOverlay()

  const isStreetViewEnabled = useIsStreetViewFeatureEnabled(hotel?.country)
  const hasMoreInfo =
    attractions?.length > 0 || (location && Object.keys(location).length > 0)

  useEffect(() => {
    if (inView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsLocationSection,
          action: Action.Viewed
        })
      )
    }
  }, [dispatchTeamEvent, inView])

  const handleClick = () => {
    if (isMobile || isDesktopXs || isDesktopSm) {
      showOverlay({
        type: OverlayType.LocationOverlay
      })
    } else {
      showOverlay({
        type: OverlayType.LocationInfoOverlay
      })
    }
  }

  const handleMoreInfoClick = () => {
    showOverlay({
      type: OverlayType.LocationInfoOverlay
    })
  }

  const handlePerson360Click = useCallback(
    (event?: React.SyntheticEvent) => {
      event?.stopPropagation()
      if (hotel?.objectID) {
        showOverlay({
          type: OverlayType.StreetView,
          params: {
            hotelId: hotel.objectID
          }
        })
      }
    },
    [hotel?.objectID]
  )

  return (
    <>
      {isMobile && (
        <LocationTitle variant="titleL" tag="h2">
          <FormattedMessageWrapper id="location" defaultMessage="Location" />
        </LocationTitle>
      )}
      <Wrapper isMobile={isMobile}>
        <StyledTrackLocationClick onClick={handleClick}>
          <MapBoxWrapper ref={ref} data-id={SelectedElement.StaticMap}>
            <StaticMapImage lon={lon} lat={lat} />
            <ButtonPositioner data-id={SelectedElement.MapButton}>
              {isStreetViewEnabled && isMobile && (
                <Button
                  size="md"
                  variant="tertiary"
                  isFloating
                  iconStart={<Icon name="Person360" size="md" />}
                  onClick={handlePerson360Click}
                />
              )}

              <Button
                size="md"
                variant="tertiary"
                isFloating
                iconStart={<Icon name={'Maximize2'} size="sm" />}
              >
                <FormattedMessageWrapper
                  id="openMap"
                  defaultMessage="Open map"
                />
              </Button>
            </ButtonPositioner>
          </MapBoxWrapper>
          <LocationAddressWrapper className="--under-map">
            {(hasMoreInfo || isMobile) && <LocationAddress hotel={hotel} />}
          </LocationAddressWrapper>
        </StyledTrackLocationClick>
        <LocationContentWrapper>
          {!isMobile && (
            <>
              <LocationTitle
                className={!hasMoreInfo && '--only-address'}
                variant="titleXL"
                tag="h2"
              >
                <FormattedMessageWrapper
                  id="location"
                  defaultMessage="Location"
                />
              </LocationTitle>
              {!hasMoreInfo && (
                <LocationAddressWrapper>
                  <LocationAddress hotel={hotel} />
                </LocationAddressWrapper>
              )}
            </>
          )}

          <HotelLocationContent
            variant={isMobile ? 'apMobile' : 'ap'}
            descriptions={hotel?.descriptions}
            attractions={attractions}
          />
          {hasMoreInfo && (
            <StyledButtonLink
              variant="primary"
              size={isMobile ? 'md' : 'lg'}
              isCompact
              onClick={handleMoreInfoClick}
            >
              <FormattedMessageWrapper
                id="location.moreInfo"
                defaultMessage="More location info"
              />
            </StyledButtonLink>
          )}
        </LocationContentWrapper>
      </Wrapper>
    </>
  )
}
